import { useAgencyCampaignContext } from '../../../agencyCampaignContext';
import { AgencyCampaignSection } from '../../../../agency/model';
import { DashboardPageFormShell } from '@shigo-ui/dashboard-ui';
import { Skeleton, toast } from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { dashboardT } from '../../../../../lib/messages';
import { returnFallback, useReturnNavigation } from '../../../../../lib/returnNavigation';
import { useCreativeMutations, useCreativeQuery } from '../../hooks';
import { createCreativeFormValue, serializeCreativeFormValue } from '../../creativeFormSchema';
import { CreativeEditorMode, CreativeTab } from '../../model';
import { CreativeEditorForm } from './CreativeEditorForm';

export function CreativeEditorPage({
  tab,
  mode,
  brandId,
  creativeId,
}: {
  tab: CreativeTab;
  mode: CreativeEditorMode;
  brandId: string;
  creativeId?: string | undefined;
}) {
  const scope = useAgencyCampaignContext();
  const editMode = mode === CreativeEditorMode.Edit;
  const query = useCreativeQuery(brandId, editMode ? creativeId : undefined);
  const mutations = useCreativeMutations();
  const listTo =
    tab === CreativeTab.Display ? '/campaigns/creatives/display' : '/campaigns/creatives/video';
  const titleKey = resolveEditorTitleKey(tab, mode);
  const backFallback = scope
    ? returnFallback({
        to: '/a/$agencyId/campaigns/$section',
        params: { agencyId: scope.agency.id, section: AgencyCampaignSection.Creatives },
        search: { brand: brandId, creativeTab: tab },
      })
    : returnFallback({ to: listTo, search: brandId ? { brand: brandId } : {} });
  const { returnTo } = useReturnNavigation(backFallback);
  const pending = mutations.create.isPending || mutations.update.isPending;
  const loadingDetail = editMode && !query.isFetchedAfterMount && !query.isError;

  async function save(values: ReturnType<typeof createCreativeFormValue>) {
    const payload = serializeCreativeFormValue(values);
    try {
      if (editMode && creativeId) {
        await mutations.update.mutateAsync({ brandId, creativeId, payload, file: values.file });
      } else {
        await mutations.create.mutateAsync({ brandId, payload, file: values.file });
      }
    } catch {
      throw new Error(dashboardT('creatives.saveError'));
    }
    toast.success(dashboardT(editMode ? 'creatives.updated' : 'creatives.created'));
    try {
      await returnTo({ replace: true });
    } catch {
      toast.error(dashboardT('creatives.redirectError'));
    }
  }

  return (
    <DashboardPageFormShell
      title={dashboardT(titleKey)}
      backAction={<ReturnBackLink fallback={backFallback} label={dashboardT('creatives.back')} />}
    >
      {!brandId ? (
        <p role="alert" className="text-destructive p-6 text-sm">
          {dashboardT('creatives.brandRequired')}
        </p>
      ) : null}
      {loadingDetail ? (
        <div
          aria-label={dashboardT('creatives.loading')}
          className="mx-auto grid w-full max-w-3xl gap-4 p-6"
        >
          <Skeleton className="h-10" />
          <Skeleton className="h-24" />
          <Skeleton className="h-48" />
        </div>
      ) : null}
      {editMode && query.isError ? (
        <p role="alert" className="text-destructive p-6 text-sm">
          {dashboardT('creatives.loadError')}
        </p>
      ) : null}
      {brandId && (!editMode || (query.isFetchedAfterMount && !query.isError && query.data)) ? (
        <CreativeEditorForm
          key={creativeId ?? 'new'}
          initialValue={createCreativeFormValue(tab, query.data)}
          editMode={editMode}
          saving={pending}
          onCancel={() => {
            void returnTo();
          }}
          onSubmit={save}
        />
      ) : null}
    </DashboardPageFormShell>
  );
}

function resolveEditorTitleKey(tab: CreativeTab, mode: CreativeEditorMode) {
  if (mode === CreativeEditorMode.Edit) {
    return tab === CreativeTab.Display ? 'creatives.edit.display' : 'creatives.edit.video';
  }
  return tab === CreativeTab.Display ? 'creatives.create.display' : 'creatives.create.video';
}
